/**
 * groupHistories 유틸리티
 * 입출금 내역을 협력사/라이더별로 그룹화하는 함수
 */
import { HeadWithdrawHistory } from '../types/headWithdraw.types';
import { ViewMode } from '../components/HistoryAccordion';

/** 그룹화된 데이터 타입 */
export interface GroupedData {
    id: string;
    name: string;
    histories: HeadWithdrawHistory[];
    totalDeposit: number;
    totalWithdrawal: number;
    totalSdcDeduct: number;
    totalHdcDeduct: number;
    currentBalance: number | null;
}

/**
 * viewMode에 따라 데이터를 그룹화
 * @param filteredHistories 이미 필터링된 내역
 * @param viewMode 현재 뷰 모드
 * @param currentBalanceByRider 라이더별 현재 잔액 맵
 * @param currentBalanceBySupplier 협력사별 현재 잔액 맵
 * @returns 그룹화된 데이터 배열
 */
export const groupHistoriesByMode = (
    filteredHistories: HeadWithdrawHistory[],
    viewMode: ViewMode,
    currentBalanceByRider?: Record<string, number>,
    currentBalanceBySupplier?: Record<string, number>
): GroupedData[] => {
    const groupMap = new Map<string, GroupedData>();

    // suppId -> suppName 매핑을 위한 맵 (협력사 모드에서 사용)
    const suppIdToNameMap = new Map<string, string>();

    // 제외할 타입 재확인 (안전장치) - SD, RD만 제외
    const excludedTypes = ['SD', 'RD'];

    // 1. 먼저 거래 내역이 있는 그룹 생성
    filteredHistories
        .filter(h => !excludedTypes.includes(h.acctType))
        .filter(h => viewMode !== 'supplier' || (h.suppName && h.suppName.trim() !== ''))
        .forEach((history) => {
            let groupId: string;
            let groupName: string;

            if (viewMode === 'supplier') {
                groupId = history.suppName || '미지정';
                groupName = history.suppName || '미지정';
                if (history.suppId && history.suppName) {
                    suppIdToNameMap.set(history.suppId, history.suppName);
                }
            } else {
                groupId = history.riderId || '미지정';
                groupName = history.riderName
                    ? `${history.riderName}(${history.riderId || '-'})`
                    : history.riderId || '미지정';
            }

            if (!groupMap.has(groupId)) {
                groupMap.set(groupId, {
                    id: groupId,
                    name: groupName,
                    histories: [],
                    totalDeposit: 0,
                    totalWithdrawal: 0,
                    totalSdcDeduct: 0,
                    totalHdcDeduct: 0,
                    currentBalance: null
                });
            }

            const group = groupMap.get(groupId)!;
            group.histories.push(history);

            const isSdc = history.acctType === 'SDC' || history.acctTypeName === 'SDC';
            const isHdc = history.acctType === 'HDC' || history.acctTypeName === 'HDC';

            if (isSdc) {
                group.totalSdcDeduct += history.amount;
            } else if (isHdc) {
                group.totalHdcDeduct += history.amount;
            } else if (viewMode === 'rider') {
                if (['RP', 'RW', 'TI', 'T', 'D', 'RR'].includes(history.acctType)) {
                    group.totalDeposit += history.amount;
                } else if (['W', 'F', 'TO'].includes(history.acctType)) {
                    group.totalWithdrawal += history.amount;
                }
            } else {
                if (['SW', 'SP', 'T', 'TI', 'SR', 'D'].includes(history.acctType)) {
                    group.totalDeposit += history.amount;
                } else if (['W', 'F', 'TO'].includes(history.acctType)) {
                    group.totalWithdrawal += history.amount;
                }
            }
        });

    // 2. 잔액 맵에 있지만 거래 내역이 없는 그룹 추가
    if (viewMode === 'rider' && currentBalanceByRider) {
        Object.keys(currentBalanceByRider).forEach(riderId => {
            if (!groupMap.has(riderId)) {
                groupMap.set(riderId, {
                    id: riderId,
                    name: `${riderId}`,
                    histories: [],
                    totalDeposit: 0,
                    totalWithdrawal: 0,
                    totalSdcDeduct: 0,
                    totalHdcDeduct: 0,
                    currentBalance: null
                });
            }
        });
    } else if (viewMode === 'supplier' && currentBalanceBySupplier) {
        Object.keys(currentBalanceBySupplier).forEach(suppId => {
            const suppName = suppIdToNameMap.get(suppId) || suppId;
            if (!groupMap.has(suppName)) {
                groupMap.set(suppName, {
                    id: suppName,
                    name: suppName,
                    histories: [],
                    totalDeposit: 0,
                    totalWithdrawal: 0,
                    totalSdcDeduct: 0,
                    totalHdcDeduct: 0,
                    currentBalance: null
                });
            }
        });
    }

    // 각 그룹 처리: 정렬 및 현재 잔액 설정
    const result = Array.from(groupMap.values()).map(group => {
        group.histories.sort((a, b) => a.no - b.no);

        if (viewMode === 'rider') {
            const riderId = group.id;
            group.currentBalance = currentBalanceByRider?.[riderId] ?? null;
        } else {
            const suppId = group.histories[0]?.suppId;
            if (suppId) {
                group.currentBalance = currentBalanceBySupplier?.[suppId] ?? null;
            } else {
                const foundSuppId = Array.from(suppIdToNameMap.entries())
                    .find(([, name]) => name === group.id)?.[0];
                group.currentBalance = foundSuppId ? (currentBalanceBySupplier?.[foundSuppId] ?? null) : null;
            }
        }

        return group;
    });

    return result.sort((a, b) => a.name.localeCompare(b.name, 'ko'));
};
